import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WeeklyMenuGrid = ({ weeklyMenu, familyMembers, onAddMeal, onRemoveMeal, onShowMealLibrary }) => {
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedMealType, setSelectedMealType] = useState(null);
  const [showAddMealModal, setShowAddMealModal] = useState(false);
  const [mealName, setMealName] = useState('');
  const [assignedTo, setAssignedTo] = useState(null);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const mealTypes = [
    { key: 'breakfast', label: 'Breakfast', icon: 'sunrise', color: '#F59E0B' },
    { key: 'lunch', label: 'Lunch', icon: 'sun', color: '#10B981' },
    { key: 'dinner', label: 'Dinner', icon: 'moon', color: '#6366F1' }
  ];

  const handleAddMealClick = (day, mealType) => {
    setSelectedDay(day);
    setSelectedMealType(mealType);
    setShowAddMealModal(true);
  };

  const handleSaveMeal = () => {
    if (!mealName?.trim()) return;

    const mealData = {
      name: mealName?.trim(),
      assignedTo: assignedTo,
      addedAt: new Date()?.toISOString()
    };

    onAddMeal(selectedDay, selectedMealType, mealData);
    setMealName('');
    setAssignedTo(null);
    setShowAddMealModal(false);
  };

  const getMember = (memberId) => {
    return familyMembers?.find(m => m?.id === memberId);
  };

  return (
    <div className="p-6">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="p-3 text-left font-semibold text-foreground border-b border-border w-32">
                Meal
              </th>
              {days?.map(day => (
                <th key={day} className="p-3 text-center font-semibold text-foreground border-b border-border">
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mealTypes?.map(mealType => (
              <tr key={mealType?.key} className="border-b border-border">
                <td className="p-3 font-medium text-foreground">
                  <div className="flex items-center gap-2">
                    <Icon name={mealType?.icon} size={18} style={{ color: mealType?.color }} />
                    <span>{mealType?.label}</span>
                  </div>
                </td>
                {days?.map(day => {
                  const meal = weeklyMenu?.[day]?.[mealType?.key];
                  return (
                    <td key={`${day}-${mealType?.key}`} className="p-2">
                      {meal ? (
                        <div className="bg-muted rounded-lg p-3 min-h-[80px] relative group hover:bg-muted/80 transition-colors">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-foreground mb-1">
                                {meal?.name}
                              </p>
                              {meal?.assignedTo && (
                                <div className="flex items-center gap-1 mt-2">
                                  <div
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: getMember(meal?.assignedTo)?.color }}
                                  />
                                  <span className="text-xs text-muted-foreground">
                                    {getMember(meal?.assignedTo)?.name}
                                  </span>
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => onRemoveMeal(day, mealType?.key)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-destructive/10 rounded"
                              title="Remove meal"
                            >
                              <Icon name="x" size={14} className="text-destructive" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleAddMealClick(day, mealType?.key)}
                          className="w-full min-h-[80px] border-2 border-dashed border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-colors flex items-center justify-center group"
                        >
                          <Icon name="plus" size={20} className="text-muted-foreground group-hover:text-primary" />
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Meal Modal */}
      {showAddMealModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                Add Meal - {selectedDay} {mealTypes?.find(m => m?.key === selectedMealType)?.label}
              </h3>
              <button
                onClick={() => setShowAddMealModal(false)}
                className="p-1 hover:bg-muted rounded"
              >
                <Icon name="x" size={20} className="text-muted-foreground" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Meal Name
                </label>
                <input
                  type="text"
                  value={mealName}
                  onChange={(e) => setMealName(e?.target?.value)}
                  placeholder="e.g., Spaghetti Bolognese"
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Assign Cooking To (Optional)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {familyMembers?.map(member => (
                    <button
                      key={member?.id}
                      onClick={() => setAssignedTo(member?.id === assignedTo ? null : member?.id)}
                      className={`p-3 border rounded-lg flex items-center gap-2 transition-colors ${
                        assignedTo === member?.id
                          ? 'border-primary bg-primary/10' :'border-border hover:bg-muted'
                      }`}
                    >
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: member?.color }}
                      />
                      <span className="text-sm text-foreground">{member?.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowAddMealModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveMeal}
                disabled={!mealName?.trim()}
                className="flex-1"
              >
                Add Meal
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklyMenuGrid;